import apiCall from "../AuthorizedApi";
import axios from "axios";
import { getHostUrl } from "../getHostUrl";

export type ListingType = "sale" | "rent" | "photographer" | "videographer";
export type BookingMode = "inquiry_only";  
export type ListingStatus = "draft" | "active" | "inactive" | "sold" | "fulfilled" | "expired";
export type BookingType = "inquiry";  
export type BookingStatus =
  | "requested"
  | "accepted"
  | "rejected"
  | "completed"
  | "cancelled";
export type RequirementType = "equipment_requirement" | "service_requirement" | "bulk_custom_unit";

export type BulkCustomUnitProjectType =
  | "equipment"
  | "team"
  | "service"
  | "bulk_manpower"
  | "other";

export type BulkCustomUnitBudgetRange =
  | "under_5k"
  | "5k_15k"
  | "15k_50k"
  | "50k_plus"
  | "flexible";

export interface BulkCustomUnitPayload {
  project_type: BulkCustomUnitProjectType;
  budget_range: BulkCustomUnitBudgetRange;
  /** Minimum 20 characters — maps to requirement description */
  description: string;
  /** ISO date string e.g. "2026-09-15" */
  date_from: string;
  location: string;
  /** Optional; auto-generated from project_type + location if omitted */
  title?: string;
  other_project_type?: string;
  custom_project_type?: string;
  custom_fields?: Array<{ label: string; value: any; key?: string }>;
  extra_fields?: Record<string, any>;
}

export interface HireTeamsOptionItem {
  value: string;
  label: string;
}

export interface HireTeamsOptionsResponse {
  project_type: HireTeamsOptionItem[];
  budget_range: HireTeamsOptionItem[];
}
export interface FilterOptionItem {
  label: string;
  value: string;
  slug?: string;
}

export interface EquipmentFilterOptions {
  category?: FilterOptionItem[];
  listing_type?: FilterOptionItem[];
  city?: FilterOptionItem[];
  location?: FilterOptionItem[];
  budget?: FilterOptionItem[];
}

export interface EquipmentRequirementsFilterOptions {
  city?: FilterOptionItem[];
  location?: FilterOptionItem[];
  budget?: FilterOptionItem[];
}

export interface TalentProfilesFilterOptions {
  location?: FilterOptionItem[];
  role?: FilterOptionItem[];
  skills?: FilterOptionItem[];
  experience?: FilterOptionItem[];
}

export interface JobHiringFilterOptions {
  location?: FilterOptionItem[];
  role?: FilterOptionItem[];
  skills?: FilterOptionItem[];
  experience?: FilterOptionItem[];
}

export type MarketplaceFilterSection = "equipment" | "equipment_requirements" | "talent_profiles" | "job_hiring";

export interface MarketplaceFilterOptionsData {
  equipment?: EquipmentFilterOptions;
  equipment_requirements?: EquipmentRequirementsFilterOptions;
  talent_profiles?: TalentProfilesFilterOptions;
  job_hiring?: JobHiringFilterOptions;
}

export interface MarketplaceFilterOptionsResponse {
  status: number;
  message: string;
  data: MarketplaceFilterOptionsData;
}
export type PriceUnit = "fixed" | "per_day" | "per_hour" | "negotiable";
export type ProfessionalType = "photographer" | "videographer" | "both";
export type ProfileType = "studio" | "freelancer";
export interface PortfolioItem {
  title: string;
  url: string;
}
export interface MarketplaceCategory {
  id: number;
  name: string;
  slug: string;
  sort_order: number;
}
export interface MarketplaceProfile {
  id: number;
  profile_type: ProfileType;
  display_name: string;
  bio?: string;
  profile_picture?: string;
  city?: string;
  state?: string;
  country?: string;
  work_categories?: string[];
  portfolio?: PortfolioItem[];
  is_public?: boolean;
  is_public_locked?: boolean;
  is_active?: boolean;
  is_seller?: boolean;        
  is_professional?: boolean;  

  professional_type?: ProfessionalType;
  profession?: string;
  experience_years?: number | null;
  skills?: string[];
  
  team_size?: number | null;
  business_info?: string;
  services_offered?: string[];
  
  contact_phone?: string;
  contact_email?: string;
  
  profile_avg_rating?: number;
  profile_total_reviews?: number;
  profile_review_count?: number;
  services_avg_rating?: number;
  services_review_count?: number;
  equipment_avg_rating?: number;
  equipment_review_count?: number;

  is_verified: boolean;
  created_at: string;
  updated_at: string;
}

export interface MarketplaceSeller {
  id: number;
  profile_type?: ProfileType;
  display_name: string;
  bio?: string;
  city?: string;
  state?: string;
  country?: string;
  is_verified: boolean;
  created_at: string;
  /** Populated after reveal — empty string when hidden */
  contact_phone?: string;
  /** Populated after reveal — empty string when hidden */
  contact_email?: string;
}

export interface MarketplaceListingImage {
  id: number | string;
  image_url: string;
  sort_order: number;
  created_at: string;
}

export interface MarketplaceListing {
  id: string;
  profile_id: number;
  seller_name: string;
  listing_type: ListingType;
  title: string;
  description?: string;
  category?: number;
  category_name?: string;
  price: string;
  price_unit: PriceUnit;
  location?: string;
  booking_mode: BookingMode;
  status: ListingStatus;
  metadata?: Record<string, any>;
  equipment?: number | null;
  seller?: MarketplaceSeller;
  image_url?: string;
  cover_image?: string | null;
  images?: MarketplaceListingImage[];
  created_at: string;
  updated_at?: string;
  /** True when the current user has already revealed contact for this listing */
  is_contact_revealed?: boolean;
  /** True when the authenticated user has saved/bookmarked this listing */
  is_saved?: boolean;
  avg_rating?: number;
  review_count?: number;
  available_from?: string;
  available_to?: string;
}

export interface MarketplaceListingListItem {
  id: string;
  profile_id: number;
  seller_name: string;
  seller_profile_type?: ProfileType;
  listing_type: ListingType;
  title: string;
  category?: number;
  category_name?: string;
  price: string;
  price_unit: PriceUnit;
  location?: string;
  booking_mode: BookingMode;
  status: ListingStatus;
  image_url?: string;
  cover_image?: string | null;
  images?: MarketplaceListingImage[];
  created_at: string;
  /** True when the authenticated user has saved/bookmarked this listing */
  is_saved?: boolean;
  avg_rating?: number;
  review_count?: number;
}

export interface MarketplaceRequirement {
  id: string;
  reference?: string;
  requirement_type: RequirementType;
  project_type?: string;
  title: string;
  description?: string;
  location?: string;
  date_from?: string;
  date_to?: string;
  budget?: string;
  budget_range?: string;
  headcount?: number;
  skills?: string[];
  duration?: string;
  crew_items?: Array<{ role: string; days?: number; notes?: string }>;
  equipment_items?: Array<{ name: string; quantity?: number; days?: number; notes?: string }>;
  custom_fields?: Array<{ key: string; label: string; value: string }>;
  applicant_count?: number;
  has_applied?: boolean;
  my_application?: RequirementApplication | null;
  status: "active" | "fulfilled" | "expired" | "draft";
  poster_name?: string;
  poster_profile_type?: ProfileType;
  profile_id?: number;
  /** Empty string when hidden; populated after reveal */
  contact_email?: string;
  /** Empty string when hidden; populated after reveal */
  contact_phone?: string;
  /** True when the current user has already revealed contact for this requirement */
  is_contact_revealed?: boolean;
  is_saved?: boolean;
  is_owner?: boolean;
  poster?: {
    profile_id?: number | string;
    display_name?: string;
    profile_picture?: string;
    city?: string;
    is_verified?: boolean;
  };
  created_at: string;
  updated_at?: string;
}



export interface MarketplaceBooking {
  id: string;
  reference: string;
  listing: string;
  listing_title: string;
  listing_type: ListingType;
  studio_name: string;
  customer_name?: string;
  customer_username?: string;
  customer_phone?: string;
  customer_country_code?: string;
  booking_type: "inquiry";
  date_from?: string;
  date_to?: string;
  quantity?: number;
  amount_due: string;
  status: BookingStatus;
  customer_notes?: string;
  studio_notes?: string;
  created_at: string;
  updated_at: string;
}



export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
  summary?: {
    avg_rating: number;
    review_count: number;
    rating_breakdown?: Record<string, number>;
  };
}



const MP = "/api/v1/marketplace";

/** Public (no-auth) axios instance */
const publicApi = axios.create({ baseURL: getHostUrl() });

const publicGet = async (url: string, params?: Record<string, any>) => {
  const res = await publicApi.get(url, { params });
  return res.data;
};



export const getMarketplaceCategories = (): Promise<MarketplaceCategory[]> =>
  publicGet(`${MP}/categories/`);



export const getMarketplaceProfile = (): Promise<{ status: boolean; data: MarketplaceProfile }> =>
  apiCall({ method: "GET", url: `${MP}/profile/` });

export const createMarketplaceProfile = (
  data: (Partial<MarketplaceProfile> & { profile_type: ProfileType }) | FormData
): Promise<{ status: boolean; data: MarketplaceProfile }> =>
  apiCall({ method: "POST", url: `${MP}/profile/`, data });

export const updateMarketplaceProfile = (
  data: Partial<MarketplaceProfile> | FormData
): Promise<{ status: boolean; data: MarketplaceProfile }> =>
  apiCall({ method: "PATCH", url: `${MP}/profile/`, data });

export const getPublicProfile = (profileId: number): Promise<{ status: boolean; data: MarketplaceProfile }> =>
  publicGet(`${MP}/profiles/${profileId}/`);

export interface ProfileQueryParams {
  profile_type?: ProfileType;
  city?: string;
  location?: string;
  professional_type?: string;
  role?: string;
  skills?: string;
  experience?: string;
  search?: string;
  page?: number;
  page_size?: number;
}

export const getPublicProfiles = (
  params?: ProfileQueryParams
): Promise<PaginatedResponse<MarketplaceProfile>> =>
  publicGet(`${MP}/profiles/`, params);




export interface ListingQueryParams {
  listing_type?: ListingType;
  profile_type?: ProfileType;   
  category?: number | string;
  location?: string;
  min_price?: number;
  max_price?: number;
  search?: string;
  ordering?: string;
  page?: number;
  page_size?: number;
}

export const getPublicListings = (
  params?: ListingQueryParams
): Promise<PaginatedResponse<MarketplaceListingListItem>> =>
  publicGet(`${MP}/listings/`, params);

export const getPublicListingDetail = (id: string): Promise<MarketplaceListing> =>
  publicGet(`${MP}/listings/${id}/`);

export interface ListingAvailabilityResponse {
  success: boolean;
  status: number;
  message: string;
  data: {
    listing_id: string;
    listing_type: string;
    equipment_id: number | null;
    date_from: string;
    date_to: string;
    unavailable_dates: string[];
    unavailable_ranges: {
      date_from: string;
      date_to: string;
      booking_id: string;
      reference: string;
      status: string;
    }[];
    blocking_statuses: string[];
  };
}

export const getListingAvailability = (
  id: string,
  params?: { date_from?: string; date_to?: string }
): Promise<ListingAvailabilityResponse> =>
  publicGet(`${MP}/listings/${id}/availability/`, params);

export const getListingDetail = (id: string): Promise<MarketplaceListing> =>
  apiCall({ method: "GET", url: `${MP}/listings/${id}/` });



export interface CreateListingPayload {
  listing_type: ListingType;
  title: string;
  description?: string;
  category?: number;
  price?: string;
  price_unit?: PriceUnit;
  location?: string;
  status?: ListingStatus;
  metadata?: Record<string, any>;
  image_url?: string;
  available_from?: string;
  available_to?: string;
}

export const createListing = (
  data: CreateListingPayload
): Promise<MarketplaceListing> =>
  apiCall({ method: "POST", url: `${MP}/listings/`, data });

export const updateListing = (
  id: string,
  data: Partial<CreateListingPayload>
): Promise<MarketplaceListing> =>
  apiCall({ method: "PATCH", url: `${MP}/listings/${id}/`, data });

export const deleteListing = (id: string): Promise<void> =>
  apiCall({ method: "DELETE", url: `${MP}/listings/${id}/` });

export const getMyListings = (params?: { page?: number; page_size?: number }): Promise<PaginatedResponse<MarketplaceListingListItem>> =>
  apiCall({ method: "GET", url: `${MP}/listings/mine/`, params });

export const unpublishListing = (id: string): Promise<{ success: boolean }> =>
  apiCall({ method: "POST", url: `${MP}/listings/${id}/unpublish/` });

export const publishFromEquipment = (
  equipmentId: number | string,
  data: {
    title?: string;
    description?: string;
    price?: string;
    price_unit?: PriceUnit;
    location?: string;
    status?: ListingStatus;
  }
): Promise<{ success: boolean; data: MarketplaceListing }> =>
  apiCall({
    method: "POST",
    url: `${MP}/listings/publish-from-equipment/${equipmentId}/`,
    data,
  });

export const toggleEquipmentMarketplaceVisibility = (
  equipmentId: number | string,
  data: { marketplace_visible: boolean }
): Promise<{ success: boolean; data: { marketplace_visible: boolean } }> =>
  apiCall({
    method: "PATCH",
    url: `${MP}/equipment/${equipmentId}/marketplace-visibility/`,
    data,
  });



export interface RequirementQueryParams {
  requirement_type?: RequirementType;
  profile_type?: ProfileType;   
  location?: string;
  city?: string;
  budget?: string;
  search?: string;
  page?: number;
  page_size?: number;
}

export interface OpenRequirementsQueryParams {
  page?: number;
  page_size?: number;
  requirement_type?: string;
  project_type?: string;
  location?: string;
  budget_range?: string;
  search?: string;
}

export const getOpenRequirements = async (
  params?: OpenRequirementsQueryParams
): Promise<PaginatedResponse<MarketplaceRequirement>> => {
  const token = localStorage.getItem("access_token");
  let res: any;
  if (token) {
    try {
      res = await apiCall({ method: "GET", url: `${MP}/open-requirements/`, params });
    } catch {
      res = null;
    }
  }

  const hasResults =
    (res?.data && Array.isArray(res.data.results) && res.data.results.length > 0) ||
    (res?.data?.data && Array.isArray(res.data.data.results) && res.data.data.results.length > 0) ||
    (res?.results && Array.isArray(res.results) && res.results.length > 0) ||
    (Array.isArray(res) && res.length > 0);

  if (!hasResults) {
    try {
      const publicRes = await publicGet(`${MP}/open-requirements/`, params);
      if (publicRes) res = publicRes;
    } catch {
      // keep existing res
    }
  }

  if (res?.data && Array.isArray(res.data.results)) {
    return {
      count: res.data.total_items ?? res.data.count ?? res.data.results.length,
      next: res.data.page && res.data.total_pages && res.data.page < res.data.total_pages ? String(res.data.page + 1) : null,
      previous: res.data.page && res.data.page > 1 ? String(res.data.page - 1) : null,
      results: res.data.results,
    };
  }
  if (res?.data?.data && Array.isArray(res.data.data.results)) {
    return {
      count: res.data.data.total_items ?? res.data.data.count ?? res.data.data.results.length,
      next: res.data.data.page && res.data.data.total_pages && res.data.data.page < res.data.data.total_pages ? String(res.data.data.page + 1) : null,
      previous: res.data.data.page && res.data.data.page > 1 ? String(res.data.data.page - 1) : null,
      results: res.data.data.results,
    };
  }
  if (res?.data && Array.isArray(res.data)) {
    return { count: res.data.length, next: null, previous: null, results: res.data };
  }
  if (res?.results && Array.isArray(res.results)) {
    return {
      count: res.count ?? res.results.length,
      next: res.next ?? null,
      previous: res.previous ?? null,
      results: res.results,
    };
  }
  if (Array.isArray(res)) {
    return { count: res.length, next: null, previous: null, results: res };
  }
  return { count: 0, next: null, previous: null, results: [] };
};

export const getOpenRequirementDetail = async (id: string): Promise<MarketplaceRequirement> => {
  const token = localStorage.getItem("access_token");
  let res: any;
  if (token) {
    res = await apiCall({ method: "GET", url: `${MP}/open-requirements/${id}/` });
  } else {
    res = await publicGet(`${MP}/open-requirements/${id}/`);
  }
  return res?.data ?? res;
};

export const getPublicRequirements = (
  params?: RequirementQueryParams
): Promise<PaginatedResponse<MarketplaceRequirement>> =>
  publicGet(`${MP}/requirements/`, params);

export const getPublicRequirementDetail = (id: string): Promise<MarketplaceRequirement> =>
  publicGet(`${MP}/requirements/${id}/`);



export const createRequirement = (
  data: Partial<MarketplaceRequirement> | any
): Promise<MarketplaceRequirement> =>
  apiCall({ method: "POST", url: `${MP}/requirements/`, data });

export const updateRequirement = (
  id: string,
  data: Partial<MarketplaceRequirement>
): Promise<MarketplaceRequirement> =>
  apiCall({ method: "PATCH", url: `${MP}/requirements/${id}/`, data });

export const deleteRequirement = (id: string): Promise<void> =>
  apiCall({ method: "DELETE", url: `${MP}/requirements/${id}/` });

export const getMyRequirements = (params?: { page?: number; page_size?: number }): Promise<PaginatedResponse<MarketplaceRequirement>> =>
  apiCall({ method: "GET", url: `${MP}/requirements/mine/`, params });



export interface CreateBookingPayload {
  listing_id: string;
  date_from?: string;
  date_to?: string;
  quantity?: number;
  customer_notes?: string;
}

export const createBooking = (
  data: CreateBookingPayload
): Promise<MarketplaceBooking> =>
  apiCall({ method: "POST", url: `${MP}/bookings/`, data });

export const getBookingDetail = (id: string): Promise<MarketplaceBooking> =>
  apiCall({ method: "GET", url: `${MP}/bookings/${id}/` });

export const getMyBookings = (params?: { page?: number; page_size?: number }): Promise<PaginatedResponse<MarketplaceBooking>> =>
  apiCall({ method: "GET", url: `${MP}/bookings/mine/`, params });

export const cancelBooking = (bookingId: string): Promise<MarketplaceBooking> =>
  apiCall({ method: "POST", url: `${MP}/bookings/${bookingId}/cancel/` });

export const getMyBookingsFiltered = (params?: {
  listing_type?: "rent" | "sale";
  status?: BookingStatus;
  page?: number;
  page_size?: number;
}): Promise<PaginatedResponse<MarketplaceBooking>> =>
  apiCall({ method: "GET", url: `${MP}/bookings/mine/`, params });



export const getStudioListings = (): Promise<{ success: boolean; data: MarketplaceListingListItem[] }> =>
  apiCall({ method: "GET", url: `${MP}/studio/listings/` });

export const getStudioBookings = (params?: { status?: BookingStatus }): Promise<{ success: boolean; data: MarketplaceBooking[] }> =>
  apiCall({ method: "GET", url: `${MP}/studio/bookings/`, params });

export const updateStudioBooking = (
  bookingId: string,
  data: { status: BookingStatus; studio_notes?: string }
): Promise<{ success: boolean; data: MarketplaceBooking }> =>
  apiCall({ method: "PATCH", url: `${MP}/studio/bookings/${bookingId}/`, data });



export type ApplicationStatus = "pending" | "accepted" | "rejected" | "withdrawn";

export interface RequirementApplication {
  id: string;
  requirement: string;
  requirement_title?: string;
  requirement_type?: RequirementType;
  requirement_status?: string;
  applicant: number;
  applicant_name?: string;
  applicant_phone?: string;
  message?: string;
  proposed_rate?: string | number;
  status: ApplicationStatus;
  owner_notes?: string;
  created_at: string;
  updated_at: string;
}

export interface ApplyToRequirementPayload {
  proposed_rate?: number | string;
  message?: string;
  company_name?: string;
  availability?: string;
  attachment?: File;
}

export const applyToRequirement = (
  requirementId: string,
  data: ApplyToRequirementPayload
): Promise<RequirementApplication> => {
  if (data.attachment) {
    const formData = new FormData();
    if (data.proposed_rate != null) formData.append("proposed_rate", String(data.proposed_rate));
    if (data.message) formData.append("message", data.message);
    if (data.company_name) formData.append("company_name", data.company_name);
    if (data.availability) formData.append("availability", data.availability);
    formData.append("attachment", data.attachment);
    return apiCall({
      method: "POST",
      url: `${MP}/requirements/${requirementId}/apply/`,
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
    });
  }
  return apiCall({ method: "POST", url: `${MP}/requirements/${requirementId}/apply/`, data });
};

export const getMyApplications = (params?: {
  page?: number;
  page_size?: number;
}): Promise<PaginatedResponse<RequirementApplication>> =>
  apiCall({ method: "GET", url: `${MP}/requirement-applications/mine/`, params });

export const getReceivedApplications = (params?: {
  status?: ApplicationStatus;
  page?: number;
  page_size?: number;
}): Promise<PaginatedResponse<RequirementApplication>> =>
  apiCall({ method: "GET", url: `${MP}/requirement-applications/received/`, params });

export const getApplicationDetail = (id: string): Promise<RequirementApplication> =>
  apiCall({ method: "GET", url: `${MP}/requirement-applications/${id}/` });

export interface UpdateApplicationPayload {
  status: ApplicationStatus;
  owner_notes?: string;
  reject_other_pending?: boolean;
  fulfill_requirement?: boolean;
}

export const updateApplication = (
  id: string,
  data: UpdateApplicationPayload
): Promise<RequirementApplication> =>
  apiCall({ method: "PATCH", url: `${MP}/requirement-applications/${id}/`, data });

export const getRequirementApplications = (
  requirementId: string
): Promise<PaginatedResponse<RequirementApplication>> =>
  apiCall({ method: "GET", url: `${MP}/requirements/${requirementId}/applications/` });



export interface ActivitySummary {
  badges: {
    rentals: number;
    inquiries: number;
    purchases: number;
    recruitment: number;
  };
  rentals: {
    total: number;
    active: number;
    completed: number;
    cancelled: number;
    rejected: number;
  };
  inquiries: {
    total: number;
    pending: number;
    accepted: number;
    rejected: number;
    completed: number;
    cancelled: number;
  };
  purchases: {
    total: number;
    pending_payment: number;
    confirmed: number;
    completed: number;
    cancelled: number;
    rejected: number;
  };
  recruitment: {
    applications_submitted: {
      total: number;
      pending: number;
      accepted: number;
      rejected: number;
      withdrawn: number;
    };
    recruitments_posted: {
      total: number;
      active: number;
      draft: number;
      fulfilled: number;
      inactive: number;
      expired: number;
    };
    applications_received: {
      total: number;
      pending: number;
      accepted: number;
      rejected: number;
      withdrawn: number;
    };
  };
}

export const getActivitySummary = (): Promise<{ success: boolean; data: ActivitySummary }> =>
  apiCall({ method: "GET", url: `${MP}/profile/activity-summary/` });



// ---------------------------------------------------------------------------
// Contact Reveal
// ---------------------------------------------------------------------------

export interface ListingRevealResponse {
  is_contact_revealed: boolean;
  seller: {
    contact_phone: string;
    contact_email: string;
  };
}

/**
 * Reveal seller contact for a listing.
 * Auth required. Returns only the revealed contact fields — caller must
 * MERGE into existing seller state (not replace) to avoid losing display_name etc.
 */
export const revealListingContact = (
  listingId: string
): Promise<ListingRevealResponse> =>
  apiCall({ method: "POST", url: `${MP}/listings/${listingId}/reveal-contact/` });

/**
 * Reveal poster contact for a requirement.
 * Auth required. Returns the full requirement with is_contact_revealed: true
 * and real contact_phone / contact_email.
 */
export const revealRequirementContact = (
  requirementId: string
): Promise<MarketplaceRequirement> =>
  apiCall({ method: "POST", url: `${MP}/requirements/${requirementId}/reveal-contact/` });

// ─── Save / Wishlist ────────────────────────────────────────────────────────────

export type SavedTargetType = "listing" | "requirement";

export interface SavedItemResponse {
  saved_id: string;
  target_type: SavedTargetType;
  listing_id: string | null;
  requirement_id: string | null;
  is_saved: boolean;
  saved_at: string;
}

export interface SavedListingSnippet {
  id: string;
  title: string;
  listing_type: ListingType;
  status: ListingStatus;
  price: string;
  price_unit: PriceUnit;
  location?: string;
  cover_image?: string | null;
  image_url?: string;
  images?: MarketplaceListingImage[];
  is_saved: boolean;
}

export interface SavedRequirementSnippet {
  id: string;
  title: string;
  requirement_type: RequirementType;
  status: string;
  budget?: string;
  location?: string;
  description?: string;
  is_saved: boolean;
}

export interface SavedItem {
  saved_id: string;
  target_type: SavedTargetType;
  saved_at: string;
  listing: SavedListingSnippet | null;
  requirement: SavedRequirementSnippet | null;
}

export interface SavedItemsQueryParams {
  target_type?: SavedTargetType;
  listing_type?: ListingType;
  requirement_type?: RequirementType;
  page?: number;
  page_size?: number;
}

export const saveListing = (listingId: string): Promise<SavedItemResponse> =>
  apiCall({ method: "POST", url: `${MP}/listings/${listingId}/save/` });

export const unsaveListing = (listingId: string): Promise<{ target_type: string; listing_id: string; is_saved: boolean }> =>
  apiCall({ method: "DELETE", url: `${MP}/listings/${listingId}/save/` });

export const saveRequirement = (requirementId: string): Promise<SavedItemResponse> =>
  apiCall({ method: "POST", url: `${MP}/requirements/${requirementId}/save/` });

export const unsaveRequirement = (requirementId: string): Promise<{ target_type: string; requirement_id: string; is_saved: boolean }> =>
  apiCall({ method: "DELETE", url: `${MP}/requirements/${requirementId}/save/` });

export const getSavedItems = (
  params?: SavedItemsQueryParams
): Promise<PaginatedResponse<SavedItem>> =>
  apiCall({ method: "GET", url: `${MP}/saved/`, params });

// ─── Reviews ──────────────────────────────────────────────────────────────────

export interface MarketplaceReview {
  id: string;
  reviewer_name: string;
  reviewer_avatar?: string;
  rating: number;
  title?: string;
  body?: string;
  seller_reply?: string;
  status: string;
  created_at: string;
  updated_at: string;
  is_mine?: boolean;
}

export interface ReviewEligibility {
  can_review: boolean;
  reason?: string;
  existing_review_id?: string;
  eligible_booking_id?: string;
}

const unwrap = (res: any) => res?.data !== undefined ? res.data : res;

export interface CreateReviewPayload {
  rating: number;
  title?: string;
  body?: string;
}

export interface ReviewQueryParams {
  page?: number;
  page_size?: number;
  target?: "listing" | "profile";
  listing_group?: "equipment" | "services";
}

export const getListingReviews = async (
  listingId: string,
  params?: ReviewQueryParams
): Promise<PaginatedResponse<MarketplaceReview>> =>
  unwrap(await apiCall({ method: "GET", url: `${MP}/listings/${listingId}/reviews/`, params }));

export const createListingReview = async (
  listingId: string,
  data: CreateReviewPayload
): Promise<MarketplaceReview> =>
  unwrap(await apiCall({ method: "POST", url: `${MP}/listings/${listingId}/reviews/`, data }));

export const getListingReviewEligibility = async (
  listingId: string
): Promise<ReviewEligibility> =>
  unwrap(await apiCall({ method: "GET", url: `${MP}/listings/${listingId}/review-eligibility/` }));

export const getProfileReviews = async (
  profileId: number | string,
  params?: ReviewQueryParams
): Promise<PaginatedResponse<MarketplaceReview>> =>
  unwrap(await apiCall({ method: "GET", url: `${MP}/profiles/${profileId}/reviews/`, params }));

export const createProfileReview = async (
  profileId: number | string,
  data: CreateReviewPayload
): Promise<MarketplaceReview> =>
  unwrap(await apiCall({ method: "POST", url: `${MP}/profiles/${profileId}/reviews/`, data }));

export const getProfileReviewEligibility = async (
  profileId: number | string
): Promise<ReviewEligibility> =>
  unwrap(await apiCall({ method: "GET", url: `${MP}/profiles/${profileId}/review-eligibility/` }));

export const getReviewDetail = async (reviewId: string): Promise<MarketplaceReview> =>
  unwrap(await apiCall({ method: "GET", url: `${MP}/reviews/${reviewId}/` }));

export const updateReview = async (
  reviewId: string,
  data: Partial<CreateReviewPayload>
): Promise<MarketplaceReview> =>
  unwrap(await apiCall({ method: "PATCH", url: `${MP}/reviews/${reviewId}/`, data }));

export const deleteReview = async (reviewId: string): Promise<void> =>
  unwrap(await apiCall({ method: "DELETE", url: `${MP}/reviews/${reviewId}/` }));

export const replyToReview = async (
  reviewId: string,
  data: { seller_reply: string }
): Promise<MarketplaceReview> =>
  unwrap(await apiCall({ method: "POST", url: `${MP}/reviews/${reviewId}/reply/`, data }));

export const getMyReviews = async (
  params?: ReviewQueryParams
): Promise<PaginatedResponse<MarketplaceReview>> =>
  unwrap(await apiCall({ method: "GET", url: `${MP}/reviews/mine/`, params }));



// ─── Hire Teams / Bulk Custom Unit ───────────────────────────────────────────

/**
 * Fetch dynamic dropdown options for the Hire Teams quote form.
 * Public endpoint — no auth required.
 */
export const getHireTeamsOptions = (): Promise<HireTeamsOptionsResponse> =>
  publicGet(`${MP}/hire-teams/options/`);

/**
 * Submit a "Build Your Custom Unit" quote request.
 * Authenticated — requires a valid JWT.
 * Equivalent to POST /api/v1/marketplace/requirements/ with requirement_type=bulk_custom_unit.
 */
export const submitHireTeamsCustomUnit = async (
  data: BulkCustomUnitPayload & Record<string, any>
): Promise<MarketplaceRequirement> => {
  const payload: any = {
    requirement_type: "bulk_custom_unit",
    project_type: data.project_type || "bulk_manpower",
    description: data.description,
    location: data.location,
    date_from: data.date_from,
    date_to: data.date_to,
    budget_range: data.budget_range,
    status: "active",
  };
  if (data.title) payload.title = data.title;
  if (data.headcount) payload.headcount = data.headcount;
  if (data.skills) payload.skills = data.skills;
  if (data.duration) payload.duration = data.duration;
  if (data.crew_items) payload.crew_items = data.crew_items;
  if (data.custom_fields) payload.custom_fields = data.custom_fields;
  if (data.extra_fields) payload.extra_fields = data.extra_fields;
  if (data.other_project_type) payload.other_project_type = data.other_project_type;
  if (data.custom_project_type) payload.custom_project_type = data.custom_project_type;

  try {
    return await apiCall({ method: "POST", url: `${MP}/requirements/`, data: payload });
  } catch (err) {
    // Fallback to /hire-teams/custom-unit/ if backend requires alias endpoint
    return await apiCall({ method: "POST", url: `${MP}/hire-teams/custom-unit/`, data: payload });
  }
};

export interface MarketplaceBanner {
  id: string;
  tagline?: string;
  headline: string;
  description?: string;
  image_url?: string;
  is_cta_enabled: boolean;
  cta_label?: string;
  cta_url?: string;
  cta_type?: "none" | "internal" | "external";
  placement: string;
  sort_order: number;
}

export const getMarketplaceBanners = async (
  placement: string = "marketplace_home"
): Promise<{ success: boolean; data: { placement: string; results: MarketplaceBanner[] } }> =>
  publicGet(`${MP}/banners/`, { placement });

// ─── Dynamic Filter Options ──────────────────────────────────────────────────

interface FilterCacheEntry {
  timestamp: number;
  data: MarketplaceFilterOptionsData;
}

const filterOptionsCache: Record<string, FilterCacheEntry> = {};
const FILTER_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch dynamic filter options for equipment, talent_profiles, and/or job_hiring.
 * Public endpoint — no auth required.
 * Caches results in-memory for 5 minutes.
 * Pass forceRefresh = true to bypass cache.
 */
export const getMarketplaceFilterOptions = async (
  section?: MarketplaceFilterSection,
  forceRefresh: boolean = false
): Promise<MarketplaceFilterOptionsData> => {
  const cacheKey = section || "all";
  const cached = filterOptionsCache[cacheKey];
  const now = Date.now();

  if (!forceRefresh && cached && now - cached.timestamp < FILTER_CACHE_TTL_MS) {
    return cached.data;
  }

  const res = await publicGet(
    `${MP}/filter-options/`,
    section ? { section } : undefined
  );

  const responseData: MarketplaceFilterOptionsData =
    res && typeof res === "object" && "data" in res ? res.data : res;

  filterOptionsCache[cacheKey] = {
    timestamp: now,
    data: responseData,
  };

  return responseData;
};

